import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Loader2, Users, CheckCircle2, ExternalLink, LayoutDashboard } from "lucide-react";
import { invokeInternal } from "./invoke";
import { useNoIndex } from "@/hooks/useNoIndex";
import { buildCollaboratorDashboardUrl } from "@/lib/adminOrigin";

type Business = { business_id: string; business_name: string };

type Dashboard = {
  id: string;
  business_id: string;
  business_name: string | null;
  collaborator_email: string | null;
  status: "active" | "pending";
  approved_at: string | null;
  created_at: string;
};

// Internal tool: invite a Dibs operator as a full-access collaborator (access
// lasts until the owner revokes it) on a client's account. Unlike the old
// act-as-owner flow, the operator logs in with
// their OWN Dibs account once the owner approves. No per-visit re-entry. The
// operator's login email is what gets linked (collaborator_email).
const CollaboratorInvitePanel = () => {
  useNoIndex();
  const navigate = useNavigate();

  const [operatorEmail, setOperatorEmail] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [business, setBusiness] = useState<Business | null>(null);
  const [busy, setBusy] = useState<null | "lookup" | "invite">(null);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<{ ownerEmail: string } | null>(null);
  const [dashboards, setDashboards] = useState<Dashboard[] | null>(null);

  const loadDashboards = useCallback(async () => {
    const { data } = await invokeInternal<{ success: boolean; dashboards?: Dashboard[] }>(
      "internal-list-collaborators",
      { include_pending: true }
    );
    if (data?.dashboards) setDashboards(data.dashboards);
  }, []);

  useEffect(() => {
    loadDashboards();
  }, [loadDashboards]);

  const lookup = useCallback(async () => {
    setError(null);
    setSent(null);
    setBusy("lookup");
    try {
      const { data, error } = await invokeInternal<{
        business_id: string;
        business_name: string;
        error?: string;
      }>("internal-business-state", { clientEmail: clientEmail.trim() });
      if (error) {
        setError(error.message);
        return;
      }
      if (!data?.business_id) {
        setError(data?.error || "No business found for that email.");
        return;
      }
      setBusiness({ business_id: data.business_id, business_name: data.business_name });
    } finally {
      setBusy((b) => (b === "lookup" ? null : b));
    }
  }, [clientEmail]);

  const invite = useCallback(async () => {
    if (!business) return;
    if (!operatorEmail.trim()) {
      setError("Enter the operator's Dibs login email.");
      return;
    }
    setError(null);
    setBusy("invite");
    try {
      const { data, error } = await invokeInternal<{
        success: boolean;
        owner_email?: string;
        error?: string;
      }>("internal-invite-collaborator", {
        business_id: business.business_id,
        collaborator_email: operatorEmail.trim(),
        origin: window.location.origin,
      });
      if (error) {
        setError(error.message);
        return;
      }
      if (!data?.success) {
        setError(data?.error || "Could not send the invite.");
        return;
      }
      setSent({ ownerEmail: data.owner_email || "the owner" });
      loadDashboards();
    } finally {
      setBusy((b) => (b === "invite" ? null : b));
    }
  }, [business, operatorEmail, loadDashboards]);

  return (
    <div className="min-h-screen bg-muted/30 px-4 py-10">
      <div className="mx-auto max-w-lg">
        <button
          type="button"
          onClick={() => navigate("/")}
          className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        <Card className="p-6 space-y-5">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            <h1 className="text-lg font-semibold">Collaborator access</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Add a Dibs operator as a full-access collaborator to help a client
            set up their dashboard. The owner approves once — access lasts until
            the owner revokes it, and the operator signs into the admin app with
            their own account.
          </p>

          {sent ? (
            <div className="rounded-lg border bg-green-50 p-4 text-sm">
              <div className="flex items-center gap-2 font-medium text-green-800">
                <CheckCircle2 className="h-4 w-4" /> Invitation sent
              </div>
              <p className="mt-2 text-muted-foreground">
                We emailed an approval link to <strong>{sent.ownerEmail}</strong>.
                Once they approve, sign in to the admin dashboard with{" "}
                <strong>{operatorEmail.trim()}</strong> — access lasts until the
                owner revokes it, and you can return any time without asking again.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => {
                  setSent(null);
                  setBusiness(null);
                  setClientEmail("");
                }}
              >
                Send another
              </Button>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="operator-email">Operator's Dibs login email</Label>
                <Input
                  id="operator-email"
                  type="email"
                  placeholder="you@dibs.team"
                  value={operatorEmail}
                  onChange={(e) => setOperatorEmail(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="client-email">Client owner email</Label>
                <div className="flex gap-2">
                  <Input
                    id="client-email"
                    type="email"
                    placeholder="owner@business.com"
                    value={clientEmail}
                    onChange={(e) => {
                      setClientEmail(e.target.value);
                      setBusiness(null);
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={lookup}
                    disabled={busy === "lookup" || !clientEmail.trim()}
                  >
                    {busy === "lookup" ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      "Look up"
                    )}
                  </Button>
                </div>
              </div>

              {business && (
                <div className="rounded-lg border p-3 text-sm">
                  Business: <strong>{business.business_name}</strong>
                </div>
              )}

              {error && <p className="text-sm text-destructive">{error}</p>}

              <Button
                className="w-full"
                onClick={invite}
                disabled={busy === "invite" || !business}
              >
                {busy === "invite" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Send approval request"
                )}
              </Button>
            </>
          )}
        </Card>

        <Card className="mt-6 p-6 space-y-4">
          <div className="flex items-center gap-2">
            <LayoutDashboard className="h-5 w-5 text-primary" />
            <h2 className="text-base font-semibold">Dashboards we can access</h2>
          </div>
          {dashboards === null ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : dashboards.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No active or pending collaborator access yet.
            </p>
          ) : (
            <ul className="space-y-2">
              {dashboards.map((d) => (
                <li
                  key={d.id}
                  className="flex items-center justify-between gap-3 rounded-lg border p-3 text-sm"
                >
                  <div className="min-w-0">
                    <div className="font-medium truncate">
                      {d.business_name || "Unnamed business"}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {d.collaborator_email}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        d.status === "active"
                          ? "bg-green-100 text-green-800"
                          : "bg-amber-100 text-amber-800"
                      }`}
                    >
                      {d.status === "active" ? "active" : "awaiting approval"}
                    </span>
                    {d.status === "active" && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          window.location.href = buildCollaboratorDashboardUrl(
                            window.location,
                            d.business_id,
                          );
                        }}
                      >
                        Open dashboard
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
};

export default CollaboratorInvitePanel;
